import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Card,
  Modal,
  TextField,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import { fadeInUp, fadeInDown, modalScaleIn } from "../../utils/animations";

const API_BASE_URL = "/api/domaines";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "85vw", md: 450 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
};

// --- SOUS-COMPOSANTS POUR LES CELLULES ---

const NomAvecStatutCell = ({ row }) => (
  <MDBox>
    <MDTypography variant="button" fontWeight="medium" color="dark">
      {row.original.nom}
    </MDTypography>
    <MDBox mt={0.4}>
      <MDBadge
        badgeContent={row.original.actif ? "Actif" : "Inactif"}
        color={row.original.actif ? "success" : "secondary"}
        variant="gradient"
        size="sm"
      />
    </MDBox>
  </MDBox>
);

NomAvecStatutCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      nom: PropTypes.string,
      actif: PropTypes.bool,
    }).isRequired,
  }).isRequired,
};

const CellWithActions = ({ row, onEdit, onDelete }) => (
  <MDBox display="flex" justifyContent="center">
    <MDButton color="info" size="small" onClick={() => onEdit(row.original)} sx={{ mr: 1 }}>
      <Icon sx={{ mr: 0.5, fontSize: 18 }}>edit</Icon>
      Modifier
    </MDButton>
    <MDButton color="error" size="small" onClick={() => onDelete(row.original.id)}>
      <Icon sx={{ mr: 0.5, fontSize: 18 }}>delete</Icon>
      Supprimer
    </MDButton>
  </MDBox>
);

CellWithActions.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      nom: PropTypes.string,
      description: PropTypes.string,
      actif: PropTypes.bool,
    }).isRequired,
  }).isRequired,
};

// --- COMPOSANT PRINCIPAL ---

function Domaines() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    nom: "",
    description: "",
    actif: true,
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(API_BASE_URL + "/");
      setRows(response.data);
    } catch (error) {
      setSnackbar({ open: true, message: "Erreur de chargement des domaines.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpen = () => {
    setIsEdit(false);
    setFormData({ id: null, nom: "", description: "", actif: true });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleEdit = useCallback((rowData) => {
    setIsEdit(true);
    setFormData(rowData);
    setOpen(true);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      setConfirmDialog({
        open: true,
        message: "Voulez-vous vraiment supprimer ce domaine ?",
        onConfirm: async () => {
          try {
            await api.delete(`${API_BASE_URL}/${id}`);
            setSnackbar({ open: true, message: "Domaine supprimé !", severity: "success" });
            fetchData();
          } catch (error) {
            const errorMsg = error.response?.data?.detail || "Erreur lors de la suppression.";
            setSnackbar({ open: true, message: errorMsg, severity: "error" });
          }
        },
      });
    },
    [fetchData]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${API_BASE_URL}/${formData.id}`, formData);
        setSnackbar({ open: true, message: "Domaine mis à jour !", severity: "success" });
      } else {
        await api.post(API_BASE_URL + "/", formData);
        setSnackbar({ open: true, message: "Domaine créé avec succès !", severity: "success" });
      }
      handleClose();
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: "Erreur lors de l'enregistrement.", severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mémorisation des colonnes pour éviter les erreurs de validation row sur les fonctions anonymes
  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id", width: "8%" },
      {
        Header: "Nom du Domaine",
        accessor: "nom",
        align: "left",
        wrap: true,
        Cell: NomAvecStatutCell,
      },
      { Header: "Description", accessor: "description", align: "left", wrap: true },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <CellWithActions row={row} onEdit={handleEdit} onDelete={handleDelete} />
        ),
      },
    ],
    [handleEdit, handleDelete]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="purple"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
              >
                <MDBox>
                  <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                    Gestion des Domaines
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
                  >
                    Gestion des domaines de classification réglementaire
                  </MDTypography>
                </MDBox>
                <MDButton variant="outlined" color="white" onClick={handleOpen}>
                  Nouveau Domaine
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Modal open={open} onClose={handleClose}>
        <Box sx={{ ...modalStyle, animation: `${modalScaleIn} 0.3s ease-out both` }}>
          <MDTypography variant="h6" mb={3}>
            {isEdit ? "Modifier Domaine" : "Nouveau Domaine"}
          </MDTypography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Nom du Domaine"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              margin="normal"
              required
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.actif}
                  onChange={handleChange}
                  name="actif"
                  color="info"
                />
              }
              label="Domaine Actif"
              sx={{ mt: 2 }}
            />
            <MDBox mt={4} display="flex" justifyContent="flex-end">
              <MDButton variant="text" color="secondary" onClick={handleClose} sx={{ mr: 1 }}>
                Annuler
              </MDButton>
              <MDButton type="submit" variant="gradient" color="purple" disabled={isSubmitting}>
                {isSubmitting ? "Envoi..." : "Enregistrer"}
              </MDButton>
            </MDBox>
          </form>
        </Box>
      </Modal>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default Domaines;
